// Security rules: one interface (RuleSet) for native TypeScript rules (defineRules) and for
// compiled firestore.rules (@estra/rules-firestore). Admin callers skip rules; everyone else
// is checked per document: get/list on reads, create/update/delete on writes.
import type { Filter } from "@estra/schema";

export type Method = "get" | "list" | "create" | "update" | "delete";

export interface RuleAuth {
  uid: string;
  /** Token claims (custom claims, email, sign-in provider …). */
  token: Record<string, unknown>;
}

/** A document as rules see it: wire-format data. */
export interface RuleDoc {
  path: string;
  id: string;
  data: Record<string, unknown>;
}

export interface Lookup {
  /** Current state of another document (null if missing). */
  get(path: string): Promise<RuleDoc | null>;
  /** State after the request's writes are applied (writes only; reads = get). */
  getAfter(path: string): Promise<RuleDoc | null>;
}

export interface RuleRequest {
  method: Method;
  /** Document path ("posts/p1/comments/c1"). */
  path: string;
  auth: RuleAuth | null;
  /** Stored document (null when missing). For list: the returned row. */
  resource: RuleDoc | null;
  /** The document as it would be after this write (create/update). */
  after: RuleDoc | null;
  /** Request time, ms. */
  time: number;
  query?: { limit?: number };
  lookup: Lookup;
  /** Optional collector: rule sets that can explain themselves (compiled firestore.rules) fill it in. */
  trace?: RuleTrace;
}

/** One step of a rules evaluation, for the console's simulator. Lines are 1-based in the rules source. */
export interface TraceEntry {
  line: number;
  kind: "match" | "allow" | "get" | "exists" | "error";
  text: string;
  result?: boolean;
}
export interface RuleTrace {
  entries: TraceEntry[];
  /** The line of the allow that granted the request (null: nothing did). */
  decidingLine: number | null;
}

/** A list query as rules see it before it runs (Firestore checks queries, not rows). */
export interface QueryRequest {
  /** Collection path ("posts", "posts/p1/comments"); for a collection group, the id with `group`. */
  collection: string;
  group: boolean;
  where?: Filter;
  limit?: number;
  auth: RuleAuth | null;
  time: number;
  lookup: Lookup;
  trace?: RuleTrace;
}

export interface RuleSet {
  allow(req: RuleRequest): Promise<boolean>;
  /**
   * Decides a whole list query from its constraints: true = every possible result is allowed,
   * false = refuse. Undefined (or no method) = check each returned row with allow().
   */
  allowQuery?(req: QueryRequest): Promise<boolean | undefined>;
}

/** Firestore's per-request ceilings on get()/exists() lookups. */
export const LOOKUP_LIMIT = { read: 10, write: 20 };

// ---- Path patterns shared by native and compiled rules ----------------------------------

export type Seg = { lit: string } | { name: string; rest: boolean };

/** "posts/{postId}/comments/{id}" or "{path=**}/comments/{id}" → segments. */
export function parsePattern(p: string): Seg[] {
  return p
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean)
    .map((s) => {
      const m = /^\{([A-Za-z_][A-Za-z0-9_]*)(=\*\*)?\}$/.exec(s);
      return m ? { name: m[1]!, rest: !!m[2] } : { lit: s };
    });
}

/** Binds a pattern to a full document path; `{x=**}` takes zero or more segments (rules v2). */
export function matchSegs(pat: Seg[], segs: string[], i = 0, j = 0, out: Record<string, string> = {}): Record<string, string> | null {
  if (i === pat.length) return j === segs.length ? out : null;
  const s = pat[i]!;
  if ("lit" in s) return segs[j] === s.lit ? matchSegs(pat, segs, i + 1, j + 1, out) : null;
  if (!s.rest) return j < segs.length ? matchSegs(pat, segs, i + 1, j + 1, { ...out, [s.name]: segs[j]! }) : null;
  for (let k = segs.length; k >= j; k--) {
    const r = matchSegs(pat, segs, i + 1, k, { ...out, [s.name]: segs.slice(j, k).join("/") });
    if (r) return r;
  }
  return null;
}

